import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { PageWrapper } from '../../../styledComponents/wrappers';
import ScanInput from './ScanInput';
import PackLineItem from './PackLineItem';
import {
  FulfillmentLineExceptionType,
  LpuOrder,
  LpuSession,
} from '../../../services/fulfillment/types';
import { EXCEPTION_TYPES } from '../../../services/fulfillment/constants';
import {
  getLpuOrder,
  getOrCreateLpuSession,
  stageOrder,
} from '../../../services/fulfillment/lpuApi';

const MockLpuStagingDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const history = useHistory();

  const [order, setOrder] = useState<LpuOrder | null>(null);
  const [session, setSession] = useState<LpuSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [focusTrigger, setFocusTrigger] = useState(0);

  const [exceptionGid, setExceptionGid] = useState<string | null>(null);
  const [selectedExcType, setSelectedExcType] = useState<FulfillmentLineExceptionType | null>(null);
  const [excNotes, setExcNotes] = useState('');

  const [staging, setStaging] = useState(false);
  const [stageResult, setStageResult] = useState<{ success: boolean; error?: string } | null>(null);

  useEffect(() => {
    const id = parseInt(orderId, 10);
    getLpuOrder(id).then(async o => {
      if (!o) {
        setError('Order not found.');
        setLoading(false);
        return;
      }
      const s = await getOrCreateLpuSession(o);
      setOrder(o);
      setSession(s);
      setLoading(false);
    });
  }, [orderId]);

  if (loading || !order || !session) {
    return (
      <PageWrapper>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
      </PageWrapper>
    );
  }

  const confirmedCount = order.lines.filter(
    l =>
      session.lineStates[l.shopifyLineItemGid]?.confirmed ||
      session.lineStates[l.shopifyLineItemGid]?.exceptionType,
  ).length;
  const allDone = confirmedCount === order.lines.length;

  const handleScan = (value: string) => {
    setScanError(null);
    const v = value.trim();
    const line = order.lines.find(l => l.sku === v || l.upc === v);
    if (!line) {
      setScanError(`"${v}" not found in this order. Check SKU or UPC.`);
      setFocusTrigger(t => t + 1);
      return;
    }
    const ls = session.lineStates[line.shopifyLineItemGid];
    if (ls?.confirmed || ls?.exceptionType) {
      setScanError(`${line.sku} already confirmed.`);
      setFocusTrigger(t => t + 1);
      return;
    }
    setSession(s => ({
      ...s!,
      lineStates: {
        ...s!.lineStates,
        [line.shopifyLineItemGid]: { ...ls, confirmed: true },
      },
    }));
    setFocusTrigger(t => t + 1);
  };

  const handleOpenException = (gid: string) => {
    setExceptionGid(gid);
    setSelectedExcType(null);
    setExcNotes('');
  };

  const handleSaveException = () => {
    if (!exceptionGid || !selectedExcType) return;
    setSession(s => ({
      ...s!,
      lineStates: {
        ...s!.lineStates,
        [exceptionGid]: {
          confirmed: false,
          exceptionType: selectedExcType,
          exceptionReason: excNotes || null,
        },
      },
    }));
    setExceptionGid(null);
  };

  const handleStage = async () => {
    setStaging(true);
    setStageResult(null);
    const result = await stageOrder(order.shopifyOrderId);
    setStaging(false);
    setStageResult(result);
  };

  if (stageResult?.success) {
    return (
      <PageWrapper>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, pb: 4 }}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            Moved to LPU Buffer
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {order.shopifyOrderName}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            {order.customer.firstName} {order.customer.lastName} will be notified for pickup.
          </Typography>
          <Button variant="contained" onClick={() => history.push('/fulfillment/mock/lpu')}>
            Back to LPU Queue
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">{order.shopifyOrderName} — Stage for Pickup</Typography>
          <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
            {order.customer.firstName} {order.customer.lastName}
          </Typography>
        </Box>
        <Button size="small" variant="text" onClick={() => history.push('/fulfillment/mock/lpu')}>
          ← LPU Queue
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {confirmedCount} of {order.lines.length} items confirmed
          </Typography>
          {allDone && <Chip label="All confirmed" color="success" size="small" />}
        </Box>
        <ScanInput
          value={scanValue}
          onChange={setScanValue}
          onScan={handleScan}
          error={scanError}
          focusTrigger={focusTrigger}
          placeholder="Scan or type SKU / UPC to confirm item…"
        />
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        {order.lines.map((line, i) => {
          const ls = session.lineStates[line.shopifyLineItemGid];
          const done = ls?.confirmed || !!ls?.exceptionType;
          const excLabel = ls?.exceptionType
            ? EXCEPTION_TYPES.find(e => e.value === ls.exceptionType)?.label
            : null;
          const badge = excLabel ? (
            <Chip label={excLabel} size="small" color="error" variant="outlined" />
          ) : ls?.confirmed ? (
            <Chip label="Confirmed" size="small" color="success" variant="outlined" />
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip label="Pending" size="small" variant="outlined" />
              <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={() => handleOpenException(line.shopifyLineItemGid)}
              >
                Exception
              </Button>
            </Box>
          );
          return (
            <Box
              key={line.shopifyLineItemGid}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                px: 2,
                py: 1.5,
                borderTop: i > 0 ? '1px solid' : 'none',
                borderColor: 'grey.200',
                bgcolor: done ? (ls?.exceptionType ? '#fff5f5' : '#f0faf4') : 'white',
              }}
            >
              <PackLineItem line={line} actions={badge} />
            </Box>
          );
        })}
      </Box>

      {stageResult && !stageResult.success && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {stageResult.error}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        disabled={!allDone || staging}
        onClick={handleStage}
        startIcon={staging ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
      >
        {staging ? 'Moving to LPU Buffer…' : 'Move to LPU Buffer'}
      </Button>
      {!allDone && (
        <Typography sx={{ fontSize: 12, color: 'grey.500', mt: 0.75 }}>
          Scan or confirm all items before staging.
        </Typography>
      )}

      {/* Exception dialog */}
      <Dialog
        open={!!exceptionGid}
        onClose={() => setExceptionGid(null)}
        fullScreen
        PaperProps={{ sx: { maxWidth: 480, mx: 'auto', height: 'auto', mt: 8, borderRadius: 2 } }}
      >
        <AppBar position="static" color="default" elevation={0}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Flag Exception
            </Typography>
            <IconButton onClick={() => setExceptionGid(null)}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {EXCEPTION_TYPES.map(et => (
            <Box
              key={et.value}
              onClick={() => setSelectedExcType(et.value as FulfillmentLineExceptionType)}
              sx={{
                p: 1.5,
                border: '2px solid',
                borderColor: selectedExcType === et.value ? 'primary.main' : 'grey.200',
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: selectedExcType === et.value ? '#f0f7ff' : 'white',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{et.label}</Typography>
            </Box>
          ))}
          <TextField
            label="Notes (optional)"
            multiline
            minRows={2}
            value={excNotes}
            onChange={e => setExcNotes(e.target.value)}
            size="small"
          />
          <Button variant="contained" disabled={!selectedExcType} onClick={handleSaveException}>
            Save Exception
          </Button>
        </Box>
      </Dialog>
    </PageWrapper>
  );
};

export default MockLpuStagingDetail;
